"use client";

import { motion } from "motion/react";
import { container, item } from "@/lib/framer";
import { MapPin, Phone, Mail, CheckCircle2, Send, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import Link from "next/link";
import { Card, CardContent } from "../ui/card";
import { generalFormSchema, GeneralFormValues } from "@/lib/schemas";
import React, { useState } from "react";
import { customZodResolver } from "@/lib/schemas";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";
import { toast } from "sonner";
import { Textarea } from "../ui/textarea";

export const Contact = () => {
  return (
    <>
      <section
        id="contact"
        className="py-16 max-sm:max-w-screen md:py-24 bg-muted"
      >
        <div className="container mx-auto px-4">
          <motion.div
            className="mb-12 text-center"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            variants={container}
          >
            <motion.h2
              className="text-3xl font-bold tracking-tight sm:text-4xl"
              variants={item}
            >
              Contact Us
            </motion.h2>
            <motion.p className="mt-4 text-muted-foreground" variants={item}>
              We&apos;d love to hear from you
            </motion.p>
          </motion.div>
          <div className="grid gap-8 md:grid-cols-2">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="mb-8 space-y-4">
                <div className="flex items-start gap-4">
                  <MapPin className="mt-1 h-5 w-5 text-primary" />
                  <div>
                    <h3 className="font-semibold">Address</h3>
                    <p className="text-muted-foreground">
                      Pokhara-23, Kaski, Nepal
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Phone className="mt-1 h-5 w-5 text-primary" />
                  <div>
                    <h3 className="font-semibold">Phone</h3>
                    <p className="text-muted-foreground">
                      +977 9808718289, +977 9846042783
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Mail className="mt-1 h-5 w-5 text-primary" />
                  <div>
                    <h3 className="font-semibold">Email</h3>
                    <p className="text-muted-foreground">
                      info@janakalyanschool.edu.np,
                      janakalyanboardingschool@gmail.com
                    </p>
                  </div>
                </div>
              </div>
              <motion.div
                className="relative h-[300px] max-sm:w-[360px] overflow-hidden rounded-lg"
                whileHover={{ scale: 1.005 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
              >
                <iframe
                  allowFullScreen
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d825.3445011099096!2d83.87777393454633!3d28.245727844379992!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3995eb8fc0539743%3A0x21ffa69f15411a33!2sShree%20Janakalyan%20English%20School!5e1!3m2!1sne!2snp!4v1741882381476!5m2!1sne!2snp"
                  width="609"
                  height="300"
                  className="border-none rounded-lg"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </motion.div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Card>
                <CardContent className="p-6">
                  <GeneralInquiryForm />
                </CardContent>
              </Card>
            </motion.div>
            {/* button link to go /contact page */}
          </div>
          <div className="flex justify-center">
            <Button className="w-full sm:w-auto mt-6 hover:scale-105 transition-all">
              <Link href="/contact">Go to Contact Page</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <motion.section
        className="bg-primary text-primary-foreground py-12"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="container mx-auto px-4 text-center">
          <motion.h2
            className="mb-4 text-3xl font-bold"
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            Ready to Join Our School Family?
          </motion.h2>
          <motion.p
            className="mb-8 mx-auto max-w-2xl"
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            Enrollment for the upcoming academic year is now open. Schedule a
            visit to our campus or contact us for more information.
          </motion.p>
          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center"
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
          >
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link href="/online-admission">
                <Button size="lg" variant="secondary">
                  Apply Online
                </Button>
              </Link>
            </motion.div>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button size="lg" variant={"secondary"}>
                Schedule a Visit
              </Button>
            </motion.div>
            {/* <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-primary text-primary hover:bg-primary/10"
                >
                  Download Brochure
                </Button>
              </motion.div> */}
          </motion.div>
        </div>
      </motion.section>
    </>
  );
};

const API_URL = "/api";

export const GeneralInquiryForm: React.FC = () => {
  const [isPending, setIsPending] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const form = useForm<GeneralFormValues>({
    resolver: customZodResolver(generalFormSchema as any),
    mode: "onChange",
    defaultValues: {
      type: "general", // Explicitly set the type
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      subject: "",
      message: "",
    },
  });

  const onSubmit = async (data: GeneralFormValues) => {
    try {
      setIsPending(true);
      const response = await fetch(`${API_URL}/contact/general`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const errorResult = await response.json();
        throw new Error(errorResult.message || "Failed to send message");
      }
      await response.json();
      form.reset();
      toast("Message Sent");
      setFormSubmitted(true);
      setIsPending(false);
    } catch (error) {
      console.error("Error sending message:", error);
      toast("Failed to send message");
      setIsPending(false);
    }
  };

  if (formSubmitted) {
    return (
      <motion.div
        className="text-center py-8"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: "spring", damping: 15 }}
      >
        <div className="mb-6 inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10">
          <CheckCircle2 className="h-8 w-8 text-primary" />
        </div>
        <h3 className="text-2xl font-bold mb-4">Message Sent Successfully!</h3>
        <p className="text-muted-foreground mb-6">
          Thank you for contacting Shree Janakalyan Boarding School. We have
          received your message and will get back to you as soon as possible.
        </p>
        <Button onClick={() => setFormSubmitted(false)}>
          Send Another Message
        </Button>
      </motion.div>
    );
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-6 max-sm:max-w-screen"
      >
        <motion.div
          className="space-y-6"
          initial="hidden"
          animate="show"
          variants={container}
        >
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
            variants={item}
          >
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>First Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your first name" {...field} />
                  </FormControl>
                  <FormMessage className="text-destructive" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your last name" {...field} />
                  </FormControl>
                  <FormMessage className="text-destructive" />
                </FormItem>
              )}
            />
          </motion.div>

          <motion.div className="space-y-2" variants={item}>
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="Enter your email address"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage className="text-destructive" />
                </FormItem>
              )}
            />
          </motion.div>

          <motion.div className="space-y-2" variants={item}>
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your phone number" {...field} />
                  </FormControl>
                  <FormMessage className="text-destructive" />
                </FormItem>
              )}
            />
          </motion.div>

          <motion.div className="space-y-2" variants={item}>
            <FormField
              control={form.control}
              name="subject"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Subject</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter the subject of your message"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage className="text-destructive" />
                </FormItem>
              )}
            />
          </motion.div>

          <motion.div className="space-y-2" variants={item}>
            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Message</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter your message"
                      rows={5}
                      className="resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage className="text-destructive" />
                </FormItem>
              )}
            />
          </motion.div>

          <motion.div variants={item}>
            <Button
              type="submit"
              className="w-full flex items-center gap-2"
              disabled={!form.formState.isValid}
            >
              {isPending ? (
                <div className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending...
                </div>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  Send Message
                </>
              )}
            </Button>
          </motion.div>
        </motion.div>
      </form>
    </Form>
  );
};
